import * as v from 'valibot';
import type { StructuredContentDocument } from '../../../cross-site/structured-content/types';
import {
  findStructuredContentByType,
  isStructuredContentType,
  toStructuredContentNodes,
} from '../../../cross-site/structured-content/utils';
import { LP_NODE_TYPES } from '../constants';

export type LpCardListNote = {
  label: string;
  body: string;
};

const noteRefSchema = v.pipe(
  v.union([v.number(), v.pipe(v.string(), v.regex(/^\d+$/))]),
  v.transform(Number),
);

export const lpCardListItemPropsSchema = v.object({
  title: v.fallback(v.string(), ''),
  imageUrl: v.fallback(v.optional(v.string()), undefined),
  mobileImageUrl: v.fallback(v.optional(v.string()), undefined),
  imageAltText: v.fallback(v.optional(v.string()), undefined),
  noteRefs: v.fallback(v.array(noteRefSchema), []),
});

export type LpCardListItem = v.InferOutput<typeof lpCardListItemPropsSchema> & {
  children: StructuredContentDocument[];
};

export type LpCardList = {
  title?: string;
  items: LpCardListItem[];
};

export function getCardListItems(children: unknown): LpCardListItem[] {
  return toStructuredContentNodes(children)
    .filter(node => isStructuredContentType(node, LP_NODE_TYPES.cardListItem))
    .map(node => ({
      ...v.parse(lpCardListItemPropsSchema, node.props),
      children: toStructuredContentNodes(node.props.children),
    }));
}

export function transformLpCardList(SCNode: unknown): LpCardList | undefined {
  const cardListNode = findStructuredContentByType<{ title?: string; children?: unknown }>(
    SCNode,
    LP_NODE_TYPES.cardList,
  );

  if (!cardListNode) {
    return undefined;
  }

  const items = getCardListItems(cardListNode.props.children);

  if (!items.length) {
    return undefined;
  }

  return { title: cardListNode.props.title || undefined, items };
}
